import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  Clock,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import { formatDistanceToNow, isPast } from 'date-fns';
import { de } from 'date-fns/locale';

interface ReviewRecord {
  id: string;
  decision_id: string;
  review_due_at: string | null;
  mandate_text: string | null;
  status: string;
  session_summary: string | null;
}

interface ReviewSectionProps {
  workspaceId: string;
  userId: string;
}

export const ReviewSection = ({ workspaceId, userId }: ReviewSectionProps) => {
  const queryClient = useQueryClient();
  const [activeReview, setActiveReview] = useState<ReviewRecord | null>(null);
  const [reviewOutcome, setReviewOutcome] = useState('');
  const [reviewSummary, setReviewSummary] = useState('');

  // Fetch execution plans with overdue reviews
  const { data: pendingReviews = [] } = useQuery({
    queryKey: ['pending-reviews', workspaceId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from('decision_execution_plans' as any) as any)
        .select('id, decision_id, review_due_at, mandate_text, status')
        .eq('workspace_id', workspaceId)
        .in('status', ['draft', 'active'])
        .not('review_due_at', 'is', null)
        .lte('review_due_at', new Date().toISOString())
        .order('review_due_at', { ascending: true })
        .limit(5);

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const sessionIds = data.map((d: any) => d.decision_id);
      const { data: sessions } = await supabase
        .from('decision_sessions')
        .select('id, summary')
        .in('id', sessionIds);

      const sessionMap = new Map((sessions || []).map(s => [s.id, s]));

      return data.map((p: any) => ({
        ...p,
        session_summary: sessionMap.get(p.decision_id)?.summary || null,
      })) as ReviewRecord[];
    },
    staleTime: 60_000,
  });

  const submitReview = useMutation({
    mutationFn: async () => {
      if (!activeReview) return;

      // Create review record
      await (supabase.from('decision_reviews' as any) as any)
        .insert({
          decision_id: activeReview.decision_id,
          workspace_id: workspaceId,
          review_type: 'scheduled',
          trigger_source: 'date',
          review_status: 'completed',
          review_outcome: reviewOutcome,
          review_summary: reviewSummary || null,
          created_by: userId,
          completed_by: userId,
          completed_at: new Date().toISOString(),
        });

      // Update execution plan based on outcome
      if (reviewOutcome === 'keep' || reviewOutcome === 'recommit') {
        // Extend review_due_at by 30 days
        const newDue = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
        await (supabase.from('decision_execution_plans' as any) as any)
          .update({ review_due_at: newDue })
          .eq('id', activeReview.id);
      } else if (reviewOutcome === 'close') {
        await (supabase.from('decision_execution_plans' as any) as any)
          .update({ status: 'completed' })
          .eq('id', activeReview.id);
      } else if (reviewOutcome === 'replace') {
        await (supabase.from('decision_execution_plans' as any) as any)
          .update({ status: 'abandoned' })
          .eq('id', activeReview.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-reviews'] });
      setActiveReview(null);
      setReviewOutcome('');
      setReviewSummary('');
    },
  });

  if (pendingReviews.length === 0) return null;

  const outcomeOptions = [
    { value: 'keep', label: 'Gilt weiterhin', icon: CheckCircle2 },
    { value: 'adjust', label: 'Operative Anpassung nötig', icon: RefreshCw },
    { value: 'recommit', label: 'Bewusst bestätigen', icon: ShieldCheck },
    { value: 'replace', label: 'Neue Entscheidung nötig', icon: AlertTriangle },
    { value: 'close', label: 'Abgeschlossen', icon: Clock },
  ];

  return (
    <>
      <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <RefreshCw className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <h3 className="text-sm font-semibold text-foreground">Anstehende Reviews</h3>
          <Badge variant="secondary" className="text-xs bg-amber-500/10 text-amber-700 dark:text-amber-300">
            {pendingReviews.length}
          </Badge>
        </div>
        <div className="space-y-2">
          {pendingReviews.map(review => (
            <button
              key={review.id}
              onClick={() => setActiveReview(review)}
              className="w-full flex items-center justify-between p-3 rounded-xl bg-background hover:bg-muted/50 border border-border/50 transition-colors text-left"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {review.session_summary || 'Entscheidung'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Review fällig seit {formatDistanceToNow(new Date(review.review_due_at!), { locale: de, addSuffix: true })}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      </div>

      <Sheet open={!!activeReview} onOpenChange={(open) => { if (!open) setActiveReview(null); }}>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
          <SheetHeader className="pb-4 border-b border-border/50">
            <SheetTitle className="text-lg">Decision Review</SheetTitle>
            <SheetDescription className="text-sm">
              {activeReview?.session_summary || 'Entscheidung'}
            </SheetDescription>
          </SheetHeader>

          <div className="pt-6 px-1 space-y-5 overflow-y-auto max-h-[calc(70vh-120px)]">
            {activeReview?.mandate_text && (
              <div className="p-3 rounded-xl bg-muted/30 border border-border/50">
                <p className="text-xs font-medium text-muted-foreground mb-1">Aktives Mandat</p>
                <p className="text-sm text-foreground">{activeReview.mandate_text}</p>
              </div>
            )}

            <div className="space-y-3">
              <h4 className="text-base font-medium text-foreground">Gilt die Entscheidung noch?</h4>
              <RadioGroup value={reviewOutcome} onValueChange={setReviewOutcome} className="space-y-2">
                {outcomeOptions.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <div key={opt.value} className="flex items-center space-x-3 p-3 rounded-xl border border-border/50 hover:bg-muted/30 transition-colors">
                      <RadioGroupItem value={opt.value} id={`review-${opt.value}`} />
                      <Label htmlFor={`review-${opt.value}`} className="text-sm cursor-pointer flex-1 flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        {opt.label}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Kommentar (optional)</h4>
              <Textarea
                placeholder="Was hat sich geändert?"
                value={reviewSummary}
                onChange={(e) => setReviewSummary(e.target.value)}
                className="min-h-[60px]"
              />
            </div>

            <Button
              className="w-full"
              disabled={!reviewOutcome || submitReview.isPending}
              onClick={() => submitReview.mutate()}
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              Review abschließen
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
